import { useState } from "react";
import { useForm, Link, router } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trash2 } from "lucide-react";
import { EmojiPickerField } from "@/components/EmojiPickerField";
import { SyncBranchModal } from "@/components/SyncBranchModal";
import type {
  PageProps,
  Service,
  ServiceCategory,
  ServicePackageOffer,
} from "@/types";
import { catalogCategoryOptions } from "./catalogCategoryOptions";

const DURATION_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hora" },
  { value: 75, label: "1 hora 15 min" },
  { value: 90, label: "1 hora 30 min" },
  { value: 105, label: "1 hora 45 min" },
  { value: 120, label: "2 horas" },
  { value: 150, label: "2 horas 30 min" },
  { value: 180, label: "3 horas" },
  { value: 240, label: "4 horas" },
];

const COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
  "#f43f5e",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#0ea5e9",
  "#3b82f6",
  "#6b7280",
];

interface BusinessOption {
  id: number;
  name: string;
}

interface ResourceOption {
  id: number;
  name: string;
}

interface ServiceFormProps {
  current_user: PageProps["current_user"];
  current_organization: PageProps["current_organization"];
  current_business: PageProps["current_business"];
  service: Service | null;
  errors?: Record<string, string>;
  businesses?: BusinessOption[];
  assigned_business_ids?: number[];
  spaces?: ResourceOption[];
  staff?: ResourceOption[];
  service_categories?: ServiceCategory[];
  package_offers?: ServicePackageOffer[];
  can_configure_catalog?: boolean;
}

export default function ServiceForm({
  service,
  errors = {},
  businesses = [],
  assigned_business_ids = [],
  spaces = [],
  staff = [],
  service_categories = [],
  package_offers = [],
  can_configure_catalog = false,
  current_user,
  current_organization,
  current_business,
}: ServiceFormProps) {
  const orgSlug = current_organization?.slug || "";
  const isEditing = !!service;

  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<number[]>(
    assigned_business_ids.length > 0
      ? assigned_business_ids
      : current_business ? [current_business.id] : []
  );
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<number[]>(
    service?.space_ids ?? []
  );
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>(
    service?.staff_assignment_ids ?? []
  );

  const toggleId = (
    list: number[],
    setList: (ids: number[]) => void,
    id: number
  ) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const [processing, setProcessing] = useState(false);
  const { data, setData } = useForm({
    name: service?.name || "",
    duration_minutes: service?.duration_minutes || 60,
    price: service?.price_cents ? (service.price_cents / 100).toString() : "",
    description: service?.description || "",
    active: service?.active ?? true,
    color: service?.color || COLORS[0],
    emoji: service?.emoji || "",
    service_category_id: service?.service_category_id?.toString() || "",
  });

  const buildFormData = (syncAll = false) => {
    const priceValue = parseFloat(data.price) || 0;
    const formData = {
      service: {
        name: data.name,
        duration_minutes: data.duration_minutes,
        price_cents: Math.round(priceValue * 100),
        description: data.description,
        active: data.active,
        color: data.color,
        emoji: data.emoji || null,
        ...(can_configure_catalog
          ? {
              service_category_id: data.service_category_id || null,
            }
          : {}),
        ...(businesses.length > 1 ? { business_ids: selectedBusinessIds } : {}),
        ...(spaces.length > 0 ? { space_ids: selectedSpaceIds } : {}),
        ...(staff.length > 0 ? { staff_assignment_ids: selectedStaffIds } : {}),
        ...(syncAll ? { sync_all: true } : {}),
      },
    };
    return formData;
  };

  const submitForm = (syncAll = false) => {
    setProcessing(true);
    const formData = buildFormData(syncAll);
    if (isEditing) {
      router.put(`/${orgSlug}/services/${service.id}`, formData, {
        onFinish: () => setProcessing(false),
      });
    } else {
      router.post(`/${orgSlug}/services`, formData, {
        onFinish: () => setProcessing(false),
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && service.sibling_count > 0) {
      setShowSyncModal(true);
      return;
    }
    submitForm();
  };

  const setField = (
    field: keyof typeof data,
    value: string | number | boolean,
  ) => {
    setData(field, value as never);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/services`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a servicios
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            {isEditing ? "Editar servicio" : "Nuevo servicio"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del servicio</Label>
              <Input
                id="name"
                placeholder="Ej: Corte de cabello"
                value={data.name}
                onChange={(e) => setField("name", e.target.value)}
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="duration">Duración</Label>
                <Select
                  value={data.duration_minutes.toString()}
                  onValueChange={(value) => setField("duration_minutes", parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar duración" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.duration_minutes && (
                  <p className="text-sm text-destructive">
                    {errors.duration_minutes}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Precio (MXN)</Label>
                <Input
                  id="price"
                  type="text"
                  inputMode="decimal"
                  placeholder="300"
                  value={data.price}
                  onChange={(e) => setField("price", e.target.value)}
                  required
                />
                {errors.price_cents && (
                  <p className="text-sm text-destructive">
                    {errors.price_cents}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Describe el servicio..."
                value={data.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Emoji (opcional)</Label>
                <EmojiPickerField
                  value={data.emoji}
                  onChange={(emoji) => setField("emoji", emoji)}
                  ariaLabel="Emoji del servicio"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                className="h-4 w-4 rounded border-input"
                checked={data.active}
                onChange={(e) => setField("active", e.target.checked)}
              />
              <Label htmlFor="active" className="font-normal">
                Servicio activo (visible para agendar)
              </Label>
            </div>

            {can_configure_catalog && (
              <div className="border-t border-border pt-4">
                <div className="space-y-2">
                  <Label htmlFor="service_category">Categoría en WhatsApp</Label>
                  <select
                    id="service_category"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={data.service_category_id}
                    onChange={(event) =>
                      setField("service_category_id", event.target.value)
                    }
                  >
                    <option value="">Otros / sin categoría</option>
                    {catalogCategoryOptions(
                      service_categories,
                      data.service_category_id,
                    ).map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.emoji ? `${category.emoji} ` : ""}
                          {category.name}
                          {!category.active ? " (oculta)" : ""}
                        </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {(spaces.length > 0 || staff.length > 0) && (
            <div className="rounded-xl border border-border bg-card p-6 space-y-6">
              {spaces.length > 0 && (
                <div className="space-y-2">
                  <Label>¿Dónde se realiza? (opcional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Sin selección = puede realizarse en cualquier espacio
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {spaces.map((space) => {
                      const selected = selectedSpaceIds.includes(space.id);
                      return (
                        <button
                          key={space.id}
                          type="button"
                          onClick={() =>
                            toggleId(selectedSpaceIds, setSelectedSpaceIds, space.id)
                          }
                          className={
                            selected
                              ? "rounded-full bg-primary px-3 py-1 text-sm font-medium text-primary-foreground"
                              : "rounded-full border border-border px-3 py-1 text-sm text-muted-foreground"
                          }
                        >
                          {space.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {staff.length > 0 && (
                <div className="space-y-2">
                  <Label>¿Quién lo realiza? (opcional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Sin selección = todo el equipo
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {staff.map((member) => {
                      const selected = selectedStaffIds.includes(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() =>
                            toggleId(selectedStaffIds, setSelectedStaffIds, member.id)
                          }
                          className={
                            selected
                              ? "rounded-full bg-primary px-3 py-1 text-sm font-medium text-primary-foreground"
                              : "rounded-full border border-border px-3 py-1 text-sm text-muted-foreground"
                          }
                        >
                          {member.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {businesses.length > 1 && (
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="space-y-2">
                <Label>Sucursales asignadas</Label>
                <p className="text-xs text-muted-foreground">
                  Selecciona en qué sucursales estará disponible este servicio
                </p>
                <div className="space-y-2 mt-2">
                  {businesses.map((biz) => (
                    <label key={biz.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBusinessIds.includes(biz.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBusinessIds([...selectedBusinessIds, biz.id]);
                          } else {
                            setSelectedBusinessIds(selectedBusinessIds.filter((id) => id !== biz.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">{biz.name}</span>
                    </label>
                  ))}
                </div>
                {selectedBusinessIds.length === 0 && (
                  <p className="text-sm text-destructive">Selecciona al menos una sucursal</p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing
                ? "Guardando..."
                : isEditing
                  ? "Guardar cambios"
                  : "Crear servicio"}
            </Button>
            <Link href={`/${orgSlug}/services`}>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>

        {isEditing && can_configure_catalog && service && (
          <PackageOffersEditor
            orgSlug={orgSlug}
            serviceId={service.id}
            initialOffers={package_offers}
          />
        )}

        <SyncBranchModal
          open={showSyncModal}
          title="Actualizar en todas las sucursales"
          description={`Este servicio existe en ${service?.sibling_count ?? 0} sucursal(es) más. ¿Deseas aplicar los cambios en todas?`}
          confirmLabel="Actualizar en todas las sucursales"
          singleLabel="Solo en esta sucursal"
          onSyncAll={() => {
            setShowSyncModal(false);
            submitForm(true);
          }}
          onSingleOnly={() => {
            setShowSyncModal(false);
            submitForm(false);
          }}
          onCancel={() => setShowSyncModal(false)}
        />
      </div>
    </DashboardLayout>
  );
}

function PackageOffersEditor({
  orgSlug,
  serviceId,
  initialOffers,
}: {
  orgSlug: string;
  serviceId: number;
  initialOffers: ServicePackageOffer[];
}) {
  const [offers, setOffers] = useState(initialOffers);
  const [draft, setDraft] = useState({
    name: "",
    sessions_included: 3,
    total_price: "",
    valid_for_days: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const csrfToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const createOffer = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(
        `/${orgSlug}/services/${serviceId}/package_offers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            service_package_offer: {
              name: draft.name,
              description: draft.description || null,
              sessions_included: draft.sessions_included,
              total_price_cents: Math.round(
                (parseFloat(draft.total_price) || 0) * 100,
              ),
              currency: "MXN",
              valid_for_days: draft.valid_for_days
                ? Number(draft.valid_for_days)
                : null,
              position: offers.length,
              active: true,
            },
          }),
        },
      );
      if (!response.ok) return;
      const payload = await response.json();
      setOffers((current) => [...current, payload.service_package_offer]);
      setDraft({
        name: "",
        sessions_included: 3,
        total_price: "",
        valid_for_days: "",
        description: "",
      });
    } finally {
      setSaving(false);
    }
  };

  const removeOffer = async (offer: ServicePackageOffer) => {
    if (!confirm(`¿Retirar el paquete "${offer.name}"?`)) return;
    const response = await fetch(
      `/${orgSlug}/services/${serviceId}/package_offers/${offer.id}`,
      { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } },
    );
    if (response.ok) {
      setOffers((current) => current.filter((item) => item.id !== offer.id));
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-6 space-y-5">
      <div>
        <h2 className="font-semibold">Paquetes exactos</h2>
        <p className="text-sm text-muted-foreground">
          Cada paquete define una cantidad exacta de sesiones y un total. No es
          una regla de descuento por volumen.
        </p>
      </div>

      {offers.length > 0 && (
        <div className="divide-y divide-border rounded-lg border border-border">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className="flex items-center justify-between gap-4 p-3"
            >
              <div>
                <p className="font-medium">{offer.name}</p>
                <p className="text-sm text-muted-foreground">
                  {offer.sessions_included} sesiones · $
                  {(offer.total_price_cents / 100).toLocaleString("es-MX")} MXN
                  {offer.valid_for_days
                    ? ` · Vigencia ${offer.valid_for_days} días`
                    : ""}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => removeOffer(offer)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={createOffer} className="grid gap-3 sm:grid-cols-2">
        <Input
          placeholder="Nombre, ej. 3 sesiones"
          value={draft.name}
          onChange={(event) =>
            setDraft((current) => ({ ...current, name: event.target.value }))
          }
          required
        />
        <Input
          type="number"
          min={1}
          placeholder="Sesiones"
          value={draft.sessions_included}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              sessions_included: Number(event.target.value),
            }))
          }
          required
        />
        <Input
          inputMode="decimal"
          placeholder="Precio total MXN"
          value={draft.total_price}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              total_price: event.target.value,
            }))
          }
          required
        />
        <Input
          type="number"
          min={1}
          placeholder="Vigencia en días (opcional)"
          value={draft.valid_for_days}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              valid_for_days: event.target.value,
            }))
          }
        />
        <textarea
          className="min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm sm:col-span-2"
          placeholder="Descripción configurada (opcional)"
          value={draft.description}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              description: event.target.value,
            }))
          }
        />
        <Button type="submit" disabled={saving} className="sm:col-span-2">
          {saving ? "Guardando..." : "Agregar paquete"}
        </Button>
      </form>
    </section>
  );
}
